import type { RegistryProposal } from './ControlProofProposals';

/** Texas franchise FTAS is a public search — TaskAssist owns self-serve upload. */
function isFranchisePublicSearchGap(row: RegistryProposal): boolean {
  const portalId = String(row.source?.portal_id || '').trim();
  const controlId = String(row.control_id || '').trim();
  const title = String(row.title || '');
  if (portalId === 'tx_comptroller') return true;
  if (controlId === 'tx-franchise-account-status') return true;
  if (/franchise|comptroller/i.test(title)) return true;
  return false;
}

/**
 * Rows that may appear under “Found possible proof.”
 * Accept is only for real evidence; credential gaps use Add credentials (no Accept).
 * Staged Do-it-for-me / smart_solution drafts never appear here.
 * Franchise public-search pauses stay in TaskAssist — not a credentials strip.
 */
export function isProofStripProposal(row: RegistryProposal): boolean {
  const kind = String(row.kind || '').trim();
  if (kind === 'smart_solution' || kind === 'obligation_watch') {
    return false;
  }
  if (kind === 'needs_credentials' || kind === 'self_serve') {
    // Do not show the gold “Credentials needed” card for public Comptroller search.
    if (isFranchisePublicSearchGap(row)) return false;
    return kind === 'needs_credentials';
  }
  if (kind === 'possible_proof') {
    return hasReviewableFileEvidence(row);
  }
  if (kind === 'connector_evidence') {
    return hasConnectorProof(row);
  }
  return false;
}

/** True when Accept is appropriate (real file or verified connector result). */
export function isAcceptableProofProposal(row: RegistryProposal): boolean {
  const kind = String(row.kind || '').trim();
  if (kind === 'possible_proof') return hasReviewableFileEvidence(row);
  if (kind === 'connector_evidence') return hasConnectorProof(row);
  return false;
}

function hasReviewableFileEvidence(row: RegistryProposal): boolean {
  const cite = String(row.cite || '').trim();
  const path = String(row.source?.path || '').trim();
  const name = String(row.source?.name || '').trim();
  if (cite.startsWith('task_action:')) return false;
  if (path || name) return true;
  if (/^(\/|~|\\\\|[A-Za-z]:\\)/.test(cite) || cite.includes('Dropbox') || cite.includes('/')) {
    return cite.length > 3;
  }
  return false;
}

function hasConnectorProof(row: RegistryProposal): boolean {
  const note = String(row.evidence_note || '');
  const right = row.source?.right_to_transact;
  if (right !== undefined && right !== null && String(right).trim() !== '') return true;
  if (/rightToTransactTX\s*=/i.test(note)) return true;
  const mode = String(row.source?.mode || '');
  return mode === 'api' || mode === 'ok' || mode === 'live';
}

export function filterReviewableProofProposals(
  proposals: RegistryProposal[],
  controlId?: string,
): RegistryProposal[] {
  return proposals.filter((row) => {
    if (controlId && row.control_id && row.control_id !== controlId) return false;
    return isProofStripProposal(row);
  });
}

/** @deprecated use isProofStripProposal */
export const isReviewableProofProposal = isProofStripProposal;
